'use client';

import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { userAppData } from '@/app/user-and-session/user-app-data';

export function useLogout() {
  const router = useRouter();
  const { update } = useSession();

  const logout = async () => {
    try {
      // Clear any session-related data from localStorage/sessionStorage
      if (typeof window !== 'undefined') {
        // Clear NextAuth's session storage and localStorage
        sessionStorage.clear();
        
        // Clear specific localStorage items
        localStorage.removeItem('build_id');
        
        // Clear any chat-related items from both storages
        const sessionKeysToRemove = [];
        const localKeysToRemove = [];
        
        // Check sessionStorage
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.startsWith('chat_') || key.includes('nextauth'))) {
            sessionKeysToRemove.push(key);
          }
        }
        
        // Check localStorage
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('chat_') || key.includes('nextauth'))) {
            localKeysToRemove.push(key);
          }
        }
        
        // Remove the keys
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
        localKeysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Clear global window functions that store user-specific state
        delete (window as any).refreshCreditCount;
        delete (window as any).refreshSubscriptionCredits;
        delete (window as any).refreshRunPodCredits;
        
        // Reset user app data singleton
        userAppData.reset();
        
        // Clear any NextAuth-related cookies manually as a backup
        // This helps ensure all session cookies are cleared
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
          if (name.includes('next-auth') || name.includes('__Secure-next-auth') || name.includes('__Host-next-auth')) {
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
            document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
          }
        });
        
        // Set logout flag for cross-tab logout
        localStorage.setItem('logout-event', Date.now().toString());
        localStorage.removeItem('logout-event');
      }

      // Use NextAuth's signOut with redirect to properly clear all tokens and cookies
      await signOut({ 
        callbackUrl: '/',
        redirect: true  // Let NextAuth handle the redirect properly
      });

    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: force redirect even if signOut fails
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }
  };

  return { logout };
}